import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { Option } from "../schema/jobForm.options";

interface CustomDropdownProps<T extends string | number> {
  options: Option<T>[] | T[];
  value?: T | null;
  onChange?: (value: T) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
}

const toOptions = <T extends string | number>(options: Option<T>[] | T[]) =>
  (options as (Option<T> | T)[]).map((option) =>
    typeof option === "object"
      ? option
      : { label: String(option), value: option },
  );

const CustomDropdown = <T extends string | number>({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  hasError,
  disabled,
}: CustomDropdownProps<T>) => {
  const items = toOptions(options);
  const selected = items.find((item) => item.value === value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        className={`flex items-center text-xss font-medium border rounded-md h-10 gap-1 px-3 outline-none w-full bg-white disabled:bg-slate-50 ${hasError ? "border-orange-700" : ""}`}
      >
        <span
          className={`text-left flex-1 ${selected ? "text-black" : "text-light-black"}`}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown size={14} color="gray" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-gray-50 max-h-44 no-scrollbar" align="start">
        <div className="flex flex-col">
          {items.map((item) => {
            return (
              <DropdownMenuItem
                key={String(item.value)}
                onSelect={() => onChange?.(item.value)}
                className={`text-xss outline-none font-medium hover:cursor-pointer hover:bg-gray-100 ${item.value === value ? "font-bold" : ""}`}
              >
                {item.label}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CustomDropdown;
